// First-class tooltips: one floating layer per view, delegated off the stable
// mount root so full-innerHTML rebuilds never re-wire anything. Driven by data
// attributes:
//   data-tip        — plain-text tooltip (replaces native `title`, which is
//                     slow to appear and unstylable)
//   data-tip-agents — rich agent-pane popover; HTML comes from the view's
//                     provider at hover time, so the content is always fresh
//   data-tip-html   — the GENERIC rich lane (bridge-ui-polish follow-up):
//                     same hover-time provider contract as agents, but the
//                     key vocabulary belongs to the mounting view (the
//                     console prefixes keys and dispatches). Providers own
//                     escaping — a key resolving to null suppresses.
// Elements carrying these MUST NOT also carry `title` (double tooltip).
// Keyboard-accessible: a KEYBOARD-VISIBLE focusin shows, focusout hides (rule
// 6 below — a script's `.focus()` is not a request for a tooltip). The layer
// is pointer-events:none so it can never trap the cursor.
//
// THE ACTIVATION CONTRACT (owner ruling 2026-09-09, #1129 — "when a user
// clicks a button, the tool tip button label should animate away"; measured
// on the Bridge: the avatar was clicked, the SETTINGS popover opened, and the
// avatar's email tip stayed painted OVER the popover's header):
//
//   1. DISMISS ON ACTIVATION. `pointerdown`, `click` and `keydown Enter|Space`
//      on the anchor dismiss its tip at once — a short fade + scale-out
//      (`.leaving`, --t-micro) that prefers-reduced-motion collapses to an
//      instant hide — and HOLD it: it does not come back until the pointer
//      (or focus) leaves the anchor and re-enters. The hold is PER MODALITY:
//      activation holds both pointer and focus; a pointer leave releases only
//      the pointer's hold, a focus leave only the focus's, and an enter is
//      refused only by its own modality's hold. Measured why (tooltip-probes
//      F, 2026-09-09): a click FOCUSES the trigger, and when the avatar menu
//      rebuilt its host Chrome fired `focusout` on the still-connected old
//      button — a single shared hold read that as "left" and the tip came
//      straight back when Escape closed the menu, pointer never having
//      moved. Keys are the anchor's id (else its tip text), so the rebuilt
//      button inherits the hold.
//   2. SUPPRESSED WHILE A POPOVER IS OPEN. An anchor that is itself an open
//      trigger (`aria-expanded="true"`, or an `aria-haspopup` trigger whose
//      `aria-controls` target is visible) shows no tip; and while ANY
//      `[aria-haspopup][aria-expanded="true"]` trigger is open, only anchors
//      INSIDE its popover (its `aria-controls` target, else the trigger's
//      parent) may show one — the `?` beside the avatar would otherwise paint
//      its tip over the popover header. `aria-haspopup` is the scope on
//      purpose: accordions and tree rows carry `aria-expanded` too, and a
//      pinned-open section must not silence every tip on the page.
//   3. STACKING BACKSTOP. The layer sits at `--z-tooltip` (150 — BELOW the
//      popover tier: .avm-menu 200, dd pops 210, ctx menu 320) so a tip that
//      does slip through rule 2 paints UNDER the popover, never over it. An
//      anchor inside an overlay (a popover, a modal, the lightbox) is raised
//      to that overlay's own stacking context + 1 at show time, so the tips
//      inside an open menu are not lost under it.
//   4. KEYBOARD PARITY. focusin shows, activation dismisses and holds exactly
//      as above, focusout clears the hold.
//   5. PLACEMENT IS THE ARRANGEMENT'S, NOT THE BUTTON'S (owner ruling
//      2026-09-09: "for these vertically stacked icons in the helm … display
//      the hover tool tip out to the right instead of overtop, that way it
//      doesn't cover up the adjacent icons"). A tip centred ABOVE a control in
//      a vertical column covers the control above it. The rule is about the
//      COLUMN, so it is declared on the column and inherited: the CSS custom
//      property `--tip-place` (top | right | left | bottom, default top), read
//      off the anchor at show time. `.rail { --tip-place: right }` therefore
//      places every control the rail holds — including ones added later, which
//      is why this is not a list of button ids. CSS is the seat rather than a
//      data attribute because the rail's phone re-orientation is PURE CSS
//      (owner ruling 2026-09-06: at <=640px the column becomes a horizontal
//      bottom tab bar), so the same media query that re-orients it returns the
//      placement to `top` — a JS-side attribute could not follow that.
//      A side placement is CENTRED on its trigger, FLIPS to the other side
//      when it would not fit, and is finally CLAMPED to the viewport, so it
//      can never run off screen or widen the page.
//   6. FOCUS TIPS ONLY WHEN THE FOCUS IS KEYBOARD-VISIBLE (owner defect
//      2026-09-09: "on popup modals … the hover text activates automatically
//      before the user hovers over a button. It looks like maybe this could be
//      because of accessibility automatically selecting the button?"). It
//      could: an open dialog MOVES FOCUS INTO ITSELF — modal-host's
//      `focusInitial()` calls `.focus()` on `[data-autofocus]`, which every
//      notice/confirm dialog puts on its primary — and `focusin` fires for a
//      script's focus exactly as it does for a Tab. So rule 4's lane painted
//      the primary's tooltip the moment the dialog opened, pointer nowhere
//      near it, which is the screenshot: a `37x29` bubble reading "OK" sitting
//      above the one icon-only OK button, read by the owner as a second
//      confirm control.
//      The autofocus is CORRECT and is not what changed — moving focus into an
//      open dialog is the a11y contract, and silencing the tip by removing it
//      would trade a cosmetic defect for a real one. What changed is the
//      TRIGGER: `focusin` now shows a tip only when the anchor matches
//      `:focus-visible`, i.e. only when the browser itself judges the focus
//      worth advertising. That is exactly the discrimination needed and
//      `:focus` cannot make it — measured on the script-focused primary
//      (modal-tip-probes M3, both stylesheets): `:focus` true,
//      `:focus-visible` FALSE after a mouse-opened dialog, and both true after
//      a keyboard-opened one, so rule 4 survives intact for keyboard users
//      (M8). The judgement is the engine's per-document input-modality bit, so
//      only a real browser can drive it — hence a probe, not a unit arm.
//
// The decision half is `tipNext` — a DOM-free reducer (the motion.ts idiom):
// helm-bridge/scripts/tooltip-state.test.mjs drives every transition from
// node, and helm-bridge/scripts/tooltip-probes.mjs drives the real layer in a
// real Chromium against the real avatar menu under both stylesheets.

import { prefersReducedMotion } from './motion.js';

export interface TipProviders {
  /** Returns tooltip HTML for a data-tip-agents key, or null to suppress. */
  agents?: (key: string) => string | null;
  /** Returns tooltip HTML for a data-tip-html key, or null to suppress. */
  html?: (key: string) => string | null;
}

const SEL = '[data-tip], [data-tip-agents], [data-tip-html]';

// ---------------------------------------------------------------------------
// The state machine. Anchors are opaque string keys — the adapter derives one
// per element; the machine never touches the DOM.

export type TipPhase = 'hidden' | 'pending' | 'shown' | 'leaving';

/** The two ways an anchor is entered and left. Each owns its own hold. */
export type TipVia = 'pointer' | 'focus';

export interface TipHold {
  pointer: string | null;
  focus: string | null;
}

export interface TipState {
  phase: TipPhase;
  /** The anchor the phase is about; null when hidden. */
  anchor: string | null;
  /** The ACTIVATED anchor, per modality: no tip for it from that modality
   *  until that modality's `leave` clears it. */
  held: TipHold;
}

export type TipEvent =
  /** Pointer or focus arrives on an anchor. `blocked` = rule 2 says no. */
  | { type: 'enter'; anchor: string; via: TipVia; blocked: boolean }
  /** The hover-intent timer for this anchor fired. */
  | { type: 'delay-done'; anchor: string }
  /** Pointer or focus leaves the anchor. */
  | { type: 'leave'; anchor: string; via: TipVia }
  /** pointerdown / click / Enter / Space on the anchor. */
  | { type: 'activate'; anchor: string }
  /** The leave transition ended (or its fallback timer fired). */
  | { type: 'hide-done' }
  /** Scroll, or the anchor left the document: hide now, keep any hold. */
  | { type: 'dismiss' };

export interface TipFx {
  /** Cancel the pending hover-intent timer. */
  cancel?: boolean;
  /** Start the hover-intent timer for this anchor. */
  schedule?: string;
  /** Render + place the tip for this anchor. */
  show?: string;
  /** 'now' drops `.on` (CSS may still fade); 'fade' is the activation leave:
   *  drop `.on`, add `.leaving`, then feed `hide-done`. */
  hide?: 'now' | 'fade';
}

const NO_HOLD: TipHold = { pointer: null, focus: null };
export const TIP_HIDDEN: TipState = { phase: 'hidden', anchor: null, held: NO_HOLD };
/** Hover intent — the delay before a tip appears (unchanged from the original layer). */
export const TIP_SHOW_DELAY_MS = 120;
/** The activation fade rides --t-micro (120ms); this is the transitionend
 *  fallback for a browser that never fires it. */
export const TIP_LEAVE_FALLBACK_MS = 240;

const NONE: TipFx = {};

export function tipNext(s: TipState, ev: TipEvent, reducedMotion: boolean): { state: TipState; fx: TipFx } {
  switch (ev.type) {
    case 'enter': {
      if (ev.blocked || ev.anchor === s.held[ev.via]) {
        // Rule 2, or rule 1's hold: nothing may show. A timer already running
        // for this anchor is dropped so it cannot fire into the hold.
        if (s.phase === 'pending' && s.anchor === ev.anchor) {
          return { state: { ...s, phase: 'hidden', anchor: null }, fx: { cancel: true } };
        }
        return { state: s, fx: NONE };
      }
      if ((s.phase === 'shown' || s.phase === 'pending') && s.anchor === ev.anchor) return { state: s, fx: NONE };
      // Fresh anchor (or the fade of another one still running): arm the timer.
      return { state: { ...s, phase: 'pending', anchor: ev.anchor }, fx: { cancel: true, schedule: ev.anchor } };
    }
    case 'delay-done': {
      // Only the enter that armed it may show — a timer landing after a leave
      // or an activation is stale.
      if (s.phase !== 'pending' || s.anchor !== ev.anchor) return { state: s, fx: NONE };
      return { state: { ...s, phase: 'shown' }, fx: { show: ev.anchor } };
    }
    case 'leave': {
      const held = s.held[ev.via] === ev.anchor ? { ...s.held, [ev.via]: null } : s.held;
      if (s.anchor !== ev.anchor) return { state: held === s.held ? s : { ...s, held }, fx: NONE };
      if (s.phase === 'pending') return { state: { phase: 'hidden', anchor: null, held }, fx: { cancel: true } };
      if (s.phase === 'shown') return { state: { phase: 'hidden', anchor: null, held }, fx: { hide: 'now' } };
      // leaving: let the fade finish; hidden: nothing to do.
      return { state: held === s.held ? s : { ...s, held }, fx: NONE };
    }
    case 'activate': {
      const held: TipHold = { pointer: ev.anchor, focus: ev.anchor };
      if (s.anchor !== ev.anchor) return { state: { ...s, held }, fx: NONE };
      if (s.phase === 'pending') return { state: { phase: 'hidden', anchor: null, held }, fx: { cancel: true } };
      if (s.phase === 'shown') {
        return reducedMotion
          ? { state: { phase: 'hidden', anchor: null, held }, fx: { hide: 'now' } }
          : { state: { ...s, phase: 'leaving', held }, fx: { hide: 'fade' } };
      }
      return { state: { ...s, held }, fx: NONE };
    }
    case 'hide-done': {
      if (s.phase !== 'leaving') return { state: s, fx: NONE };
      return { state: { ...s, phase: 'hidden', anchor: null }, fx: NONE };
    }
    case 'dismiss': {
      if (s.phase === 'hidden') return { state: s, fx: NONE };
      return { state: { ...s, phase: 'hidden', anchor: null }, fx: { cancel: true, hide: 'now' } };
    }
  }
}

// ---------------------------------------------------------------------------
// Rule 2, read off the live document. Every DOM touch is guarded so the
// zero-DOM smoke (a stub root, a stub anchor) can still drive the layer.

const attr = (e: Element | null, name: string): string | null =>
  e && typeof e.getAttribute === 'function' ? e.getAttribute(name) : null;

const isVisible = (e: HTMLElement): boolean =>
  !e.hidden && (typeof e.getClientRects !== 'function' || e.getClientRects().length > 0);

/** The element an `aria-controls` names, when it is in the document. */
const controlled = (trigger: Element): HTMLElement | null => {
  const id = attr(trigger, 'aria-controls');
  if (!id || typeof document === 'undefined' || typeof document.getElementById !== 'function') return null;
  return document.getElementById(id) as HTMLElement | null;
};

const POPOVER_ROLES = new Set(['menu', 'listbox', 'dialog']);

/** Is `t` inside an OPEN popover — a visible menu/listbox/dialog ancestor?
 *  Asked of the ANCHOR rather than of some trigger's subtree, because a
 *  popover need not live under its trigger: #1125/#1135 portals the avatar
 *  menu to <body> (fixed, anchored through custom properties) to escape the
 *  header's backdrop-filter stacking context, and it carries no
 *  `aria-controls`, so a containment test rooted at the trigger reports every
 *  control INSIDE that menu as "outside the popover" and silences its tips.
 *  Walking up from the anchor is portal-proof by construction. */
function inOpenPopover(t: HTMLElement): boolean {
  for (let n: HTMLElement | null = t.parentElement; n; n = n.parentElement) {
    const role = attr(n, 'role');
    if (((role && POPOVER_ROLES.has(role)) || n.hasAttribute?.('popover')) && isVisible(n)) return true;
  }
  return false;
}

/** True when `t` may not show a tip because a popover is in play. */
export function popoverBlocks(t: HTMLElement): boolean {
  if (attr(t, 'aria-expanded') === 'true') return true;
  const own = controlled(t);
  if (own && isVisible(own) && (attr(t, 'aria-haspopup') !== null || POPOVER_ROLES.has(attr(own, 'role') ?? '') || own.hasAttribute?.('popover'))) {
    return true;
  }
  if (typeof document === 'undefined' || typeof document.querySelectorAll !== 'function') return false;
  // An anchor INSIDE an open popover keeps its tip; one outside would paint
  // over it — that is the screenshot.
  if (inOpenPopover(t)) return false;
  for (const trig of document.querySelectorAll<HTMLElement>('[aria-haspopup][aria-expanded="true"]')) {
    if (trig === t) return true;
    const host = controlled(trig) ?? trig.parentElement;
    if (host && typeof host.contains === 'function' && host.contains(t)) continue;
    return true;
  }
  return false;
}

/**
 * Rule 6, read off the live element. True when a `focusin` on `t` may show a
 * tip — i.e. when the browser judges this focus KEYBOARD-VISIBLE.
 *
 * Fails OPEN in two named cases, both of which restore the pre-rule-6
 * behaviour rather than silently killing keyboard tooltips:
 *   - no `matches` (the zero-DOM stub root the smoke drives the layer over);
 *   - `:focus-visible` unsupported, which throws SyntaxError on a browser old
 *     enough not to know the selector.
 * A browser that cannot answer keeps rule 4 whole; one that can, gates.
 */
export function focusIsKeyboardVisible(t: HTMLElement): boolean {
  if (typeof t.matches !== 'function') return true;
  try {
    return t.matches(':focus-visible');
  } catch {
    return true;
  }
}

// ---------------------------------------------------------------------------
// Rule 5: placement. Pure geometry so the suites can drive it without a
// browser; the adapter supplies the rects.

export type TipPlace = 'top' | 'right' | 'bottom' | 'left';

export interface TipRect { top: number; left: number; right: number; bottom: number; width: number; height: number; }

/** Gap between the anchor and the tip, and the minimum distance to any
 *  viewport edge. */
export const TIP_GAP = 8;
export const TIP_EDGE = 8;

export function readTipPlace(t: HTMLElement): TipPlace {
  if (typeof getComputedStyle !== 'function') return 'top';
  const v = getComputedStyle(t).getPropertyValue('--tip-place').trim();
  return v === 'right' || v === 'left' || v === 'bottom' ? v : 'top';
}

/**
 * Where the tip goes, given the anchor's rect, the tip's own size and the
 * viewport. A SIDE placement is centred on the anchor's vertical midpoint and
 * flips to the opposite side when it would not fit; a TOP/BOTTOM placement
 * keeps the original behaviour (centred horizontally, flipped down when it
 * would leave the top). Both are finally CLAMPED into the viewport, so the tip
 * can never run off screen — the layer is `position: fixed`, and a clamped box
 * is what keeps the page from ever scrolling horizontally.
 */
export function tipPosition(
  place: TipPlace,
  r: TipRect,
  tip: { width: number; height: number },
  vw: number,
  vh: number,
): { x: number; y: number; placed: TipPlace } {
  const clampX = (x: number): number => Math.max(TIP_EDGE, Math.min(x, vw - tip.width - TIP_EDGE));
  const clampY = (y: number): number => Math.max(TIP_EDGE, Math.min(y, vh - tip.height - TIP_EDGE));
  if (place === 'right' || place === 'left') {
    const y = clampY(r.top + r.height / 2 - tip.height / 2);
    const xRight = r.right + TIP_GAP;
    const xLeft = r.left - tip.width - TIP_GAP;
    const fitsRight = xRight + tip.width <= vw - TIP_EDGE;
    const fitsLeft = xLeft >= TIP_EDGE;
    const first = place === 'right' ? fitsRight : fitsLeft;
    const other = place === 'right' ? fitsLeft : fitsRight;
    let placed: TipPlace = place;
    if (!first && other) placed = place === 'right' ? 'left' : 'right';
    return { x: clampX(placed === 'right' ? xRight : xLeft), y, placed };
  }
  const x = clampX(r.left + r.width / 2 - tip.width / 2);
  const above = r.top - tip.height - TIP_GAP;
  const placed: TipPlace = place === 'bottom' || above < TIP_EDGE ? 'bottom' : 'top';
  return { x, y: clampY(placed === 'bottom' ? r.bottom + TIP_GAP : above), placed };
}

// ---------------------------------------------------------------------------
// The adapter.

export function installTooltips(root: HTMLElement, providers: TipProviders = {}): () => void {
  let el: HTMLDivElement | null = null;
  let showT = 0;
  let hideT = 0;
  let cur: HTMLElement | null = null;
  /** The element behind the machine's current anchor key (schedule/show need it). */
  let pendingEl: HTMLElement | null = null;
  let state: TipState = TIP_HIDDEN;

  // Anchor keys: the id when there is one, else the tip text — so a trigger
  // rebuilt by its own menu (same id, new element) keeps rule 1's hold.
  const keyOf = (t: HTMLElement): string => {
    if (t.id) return `#${t.id}`;
    const d = t.dataset ?? {};
    return `tip:${d.tipAgents ?? d.tipHtml ?? d.tip ?? ''}`;
  };

  const layer = (): HTMLDivElement => {
    if (!el) {
      el = document.createElement('div');
      el.className = 'bridge-tip';
      el.setAttribute('role', 'tooltip');
      document.body.appendChild(el);
    }
    return el;
  };

  /** Rule 3's raise: an anchor inside a stacking context at z >= the layer's
   *  own seat lifts the layer to z + 1, so tips inside an open menu are not
   *  lost under it. Anywhere else the seat (`--z-tooltip`) stands. */
  const raise = (target: HTMLElement, t: HTMLDivElement): void => {
    t.style.zIndex = '';
    if (typeof getComputedStyle !== 'function') return;
    const base = parseInt(getComputedStyle(t).zIndex, 10) || 0;
    let z = 0;
    for (let n: HTMLElement | null = target; n && n !== document.body; n = n.parentElement) {
      const v = parseInt(getComputedStyle(n).zIndex, 10);
      if (!Number.isNaN(v) && v > z) z = v;
    }
    if (z + 1 > base) t.style.zIndex = String(z + 1);
  };

  /** Render + place. Returns false when nothing is to be shown (a provider
   *  suppressed) — the machine is then told to dismiss. */
  const render = (target: HTMLElement): boolean => {
    const t = layer();
    const agentsKey = target.dataset.tipAgents;
    const htmlKey = target.dataset.tipHtml;
    if (agentsKey !== undefined) {
      const html = providers.agents?.(agentsKey);
      if (!html) return false;
      t.innerHTML = html;
      t.classList.add('rich');
    } else if (htmlKey !== undefined && providers.html) {
      // The html lane DEGRADES to the element's own data-tip when the
      // provider suppresses (null) — a rich carrier may ship both, so a
      // view mounted without the provider still tooltips in plain text.
      const html = providers.html(htmlKey);
      if (html) {
        t.innerHTML = html;
        t.classList.add('rich');
      } else if (target.dataset.tip) {
        t.textContent = target.dataset.tip;
        t.classList.remove('rich');
      } else return false;
    } else if (target.dataset.tip) {
      t.textContent = target.dataset.tip;
      t.classList.remove('rich');
    } else return false;
    cur = target;
    window.clearTimeout(hideT);
    t.classList.remove('leaving');
    // Place after content is in (needs real dimensions): centered above the
    // anchor, flipped below when it would leave the viewport, clamped to the
    // window edges.
    t.classList.add('on');
    raise(target, t);
    const r = target.getBoundingClientRect();
    const tr = t.getBoundingClientRect();
    const { x, y, placed } = tipPosition(readTipPlace(target), r, tr, window.innerWidth, window.innerHeight);
    t.style.left = `${Math.round(x)}px`;
    t.style.top = `${Math.round(y)}px`;
    // The placement actually used, for the probe and for any style that wants
    // to point an arrow the right way.
    t.setAttribute('data-place', placed);
    return true;
  };

  const hideLayer = (mode: 'now' | 'fade'): void => {
    window.clearTimeout(hideT);
    cur = null;
    if (!el) return;
    const t = el;
    t.classList.remove('on');
    if (mode !== 'fade') {
      t.classList.remove('leaving');
      return;
    }
    // The activation leave: `.leaving` carries the scale-out; transitionend
    // (or the fallback) hands the machine `hide-done`. A render in between
    // (another anchor) already cleared the class and the timer.
    t.classList.add('leaving');
    const done = (): void => {
      window.clearTimeout(hideT);
      t.removeEventListener?.('transitionend', onEnd);
      if (!t.classList.contains('on')) t.classList.remove('leaving');
      feed({ type: 'hide-done' });
    };
    const onEnd = (e: Event): void => {
      if (e.target === t && (e as TransitionEvent).propertyName === 'opacity') done();
    };
    t.addEventListener?.('transitionend', onEnd);
    hideT = window.setTimeout(done, TIP_LEAVE_FALLBACK_MS);
  };

  const apply = (fx: TipFx): void => {
    if (fx.cancel) {
      window.clearTimeout(showT);
      showT = 0;
    }
    if (fx.schedule !== undefined) {
      const target = pendingEl;
      const key = fx.schedule;
      showT = window.setTimeout(() => {
        if (target && target === pendingEl) feed({ type: 'delay-done', anchor: key });
      }, TIP_SHOW_DELAY_MS);
    }
    if (fx.hide) hideLayer(fx.hide);
    if (fx.show !== undefined && pendingEl && !render(pendingEl)) feed({ type: 'dismiss' });
  };

  const feed = (ev: TipEvent): void => {
    const r = tipNext(state, ev, prefersReducedMotion());
    state = r.state;
    apply(r.fx);
    el?.setAttribute('data-phase', state.phase);
  };

  const anchorOf = (e: Event): HTMLElement | null => {
    const t = e.target as HTMLElement | null;
    return t && typeof t.closest === 'function' ? t.closest<HTMLElement>(SEL) : null;
  };

  const enter = (e: Event, via: TipVia): void => {
    const target = anchorOf(e);
    if (!target) return;
    pendingEl = target;
    feed({ type: 'enter', anchor: keyOf(target), via, blocked: popoverBlocks(target) });
  };
  const over = (e: Event): void => enter(e, 'pointer');
  // Rule 6: a script's `.focus()` — a dialog moving focus into itself on open —
  // is not a request for a tooltip. Only a focus the browser would paint a
  // ring for shows one.
  const focusin = (e: Event): void => {
    const target = anchorOf(e);
    if (target && !focusIsKeyboardVisible(target)) return;
    enter(e, 'focus');
  };
  const out = (e: MouseEvent): void => {
    const target = anchorOf(e);
    if (!target) return;
    const to = e.relatedTarget as HTMLElement | null;
    if (to && target.contains(to)) return;
    feed({ type: 'leave', anchor: keyOf(target), via: 'pointer' });
  };
  // A focus leave releases the FOCUS hold only. Chrome fires this on a
  // focused trigger its own menu is rebuilding (still connected at dispatch,
  // measured) — the pointer resting on the new button keeps its own hold.
  const focusout = (e: Event): void => {
    const target = anchorOf(e);
    if (target) feed({ type: 'leave', anchor: keyOf(target), via: 'focus' });
    else feed({ type: 'dismiss' });
  };
  // Rule 1 — CAPTURE phase on purpose: a trigger's own handler may stop
  // propagation (avatar-menu.ts does), and the tip must still hear it.
  const activate = (e: Event): void => {
    const target = anchorOf(e);
    if (target) feed({ type: 'activate', anchor: keyOf(target) });
  };
  const keyActivate = (e: KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') activate(e);
  };
  const dismiss = (): void => feed({ type: 'dismiss' });
  // A rebuild can replace the anchor out from under a visible tip — sweep it
  // on the next mouse move instead of leaving it orphaned mid-screen.
  const sweep = (): void => {
    if (cur && !cur.isConnected) dismiss();
  };

  root.addEventListener('mouseover', over);
  root.addEventListener('mouseout', out);
  root.addEventListener('focusin', focusin);
  root.addEventListener('focusout', focusout);
  root.addEventListener('pointerdown', activate, true);
  root.addEventListener('click', activate, true);
  root.addEventListener('keydown', keyActivate, true);
  root.addEventListener('mousemove', sweep, { passive: true });
  root.addEventListener('scroll', dismiss, true);
  return () => {
    root.removeEventListener('mouseover', over);
    root.removeEventListener('mouseout', out);
    root.removeEventListener('focusin', focusin);
    root.removeEventListener('focusout', focusout);
    root.removeEventListener('pointerdown', activate, true);
    root.removeEventListener('click', activate, true);
    root.removeEventListener('keydown', keyActivate, true);
    root.removeEventListener('mousemove', sweep);
    root.removeEventListener('scroll', dismiss, true);
    window.clearTimeout(showT);
    window.clearTimeout(hideT);
    el?.remove();
    el = null;
    cur = null;
    pendingEl = null;
    state = TIP_HIDDEN;
  };
}
